<script lang="ts">
  /**
   * ScoresRingButton — the scores ring, top right on every tab (Scott,
   * 2026-09-10: "We have room for two icons top right. The share and the
   * scores"; Q3: Club included). Reads like the Oura app's small ring: a thin
   * track, an arc in the accent color, a 44 pt target. Tap opens the scores
   * drawer.
   *
   * Arc = this week's meters / the weekly goal the swimmer set. No goal: the
   * track alone, no arc (weekGoalRing). Goal met: a full arc in --win.
   */
  import { workouts } from "../lib/workoutsStore";
  import { weekDistanceMeters } from "../lib/aggregates";
  import { activeOverlay, goalPrefs, openOverlay, weekGoalRing } from "../lib/overlay";
  import { haptic } from "../lib/haptics";

  let ring = $derived(weekGoalRing(weekDistanceMeters($workouts), $goalPrefs?.weekDistanceMeters));

  // r = 10.5 in a 26 box, 3 stroke: thin, but the full track stays visible
  // under the arc so a partial arc reads as progress, not a loading spinner
  // (VP panel round 1, 4/5).
  const R = 10.5;
  const CIRC = 2 * Math.PI * R;
  // One color for one number (VP ruling): the drawer ring's orange gradient
  // (HistoryRings' #dist-grad). Per-instance id so two rings never share defs.
  const gradId = `scores-ring-grad-${Math.random().toString(36).slice(2, 8)}`;

  function openScores() {
    haptic("light");
    openOverlay({ kind: "scores" });
  }
</script>

<button
  type="button"
  class="ring-btn"
  aria-label={ring.label}
  aria-expanded={$activeOverlay?.kind === "scores"}
  data-ring-fill={ring.fill === null ? "none" : ring.fill.toFixed(3)}
  onclick={openScores}
>
  <svg viewBox="0 0 26 26" width="26" height="26" aria-hidden="true">
    <defs>
      <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#FF7A45" />
        <stop offset="100%" stop-color="#FBC02D" />
      </linearGradient>
    </defs>
    <circle class="track" cx="13" cy="13" r={R} fill="none" stroke-width="3" />
    {#if ring.fill !== null && ring.fill > 0}
      <circle
        cx="13"
        cy="13"
        r={R}
        fill="none"
        stroke={`url(#${gradId})`}
        stroke-width="3"
        stroke-linecap="round"
        stroke-dasharray={`${ring.fill * CIRC} ${CIRC}`}
        transform="rotate(-90 13 13)"
      />
    {/if}
  </svg>
</button>

<style>
  .ring-btn {
    width: 44px;
    height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: var(--text-primary);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: background-color var(--dur-micro) var(--ease-swim),
                transform var(--dur-micro) var(--ease-swim);
  }
  .ring-btn:hover {
    background: var(--bg-raised);
  }
  .ring-btn:active {
    transform: scale(0.94);
  }
  .ring-btn:focus-visible {
    outline: 2px solid var(--accent-yellow);
    outline-offset: 2px;
  }
  svg {
    display: block;
  }
  /* A visible full track (Oura's small ring): brighter than HistoryRings'
     0.10 because at 26 px a faint track vanishes and the arc alone spins. */
  .track {
    stroke: rgba(255, 255, 255, 0.24);
  }
</style>
